/**
 * ConferenceManager: 회의 최상위 오케스트레이터
 *
 * 기존 wehago-conference의 SVConference/ConferenceManager.js (514줄)를
 * TypeScript로 리팩토링.
 *
 * 역할:
 * - JitsiMeetJS 초기화
 * - Connection(XMPP) + ConferenceConnector(룸) 조율
 * - VideoPage에 단순화된 API 제공
 */
import type { ConferenceHandlers, IConferenceManager } from './types';
import type { JitsiConference, JitsiRemoteTrack } from '@/types/jitsi';
import Connection from './Connection';
import ConferenceConnector from './ConferenceConnector';
import config from './config';
import { CONFERENCE_COMMANDS } from './commands';
import { isRecorderMode } from '@/utils/recorderMode';
import { localActions } from '@/store/slices/localSlice';
import { store } from '@/store';
import apiManager from '@/api';

class ConferenceManager implements IConferenceManager {
  private _connection: Connection | null = null;
  private _connector: ConferenceConnector | null = null;
  private _handlers: ConferenceHandlers | null = null;
  private _isDisposed: boolean = false;
  private _deviceChangeHandler: (() => void) | null = null;
  private _deviceChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastDefaultAudioInputLabel: string = '';

  // VideoPage에서 주입하는 원격 트랙 콜백 (TrackContext 업데이트용)
  onRemoteTrackAdded?: (participantId: string, track: JitsiRemoteTrack) => void;
  onRemoteTrackRemoved?: (participantId: string, track: JitsiRemoteTrack) => void;
  onParticipantLeft?: (participantId: string) => void;

  // ─── 초기화 ───────────────────────────────────────────────────────────────

  private _initJitsiMeetJS(): void {
    window.JitsiMeetJS.init({
      disableAudioLevels: false,
      enableWindowOnErrorHandler: false,
      enableAnalyticsLogging: false,
      disableThirdPartyRequests: true,
      disableAP: false,
      disableAEC: false,
      disableNS: false,
      disableAGC: false,
    });

    // recorder(Jibri)는 BridgeChannel VideoSourcesMap/AudioSourcesMap 등 진단용 INFO 로그가 필요하다.
    // 일반 참가자는 콘솔 노이즈를 막기 위해 WARN을 유지한다.
    window.JitsiMeetJS.setLogLevel(
      isRecorderMode() ? window.JitsiMeetJS.logLevels.INFO : window.JitsiMeetJS.logLevels.WARN
    );
  }

  // ─── 연결 ────────────────────────────────────────────────────────────────

  async connect(
    roomId: string,
    userName: string,
    handlers: ConferenceHandlers,
    options?: {
      mediaServerToken?: string;
      wehagoId?: string;
      nickname?: string;
      companyCode?: string;
      avatarUrl?: string;
      selectedAvatar?: string;
      nationality?: string;
      userNo?: string;
      startVideoMuted?: boolean;
      startAudioMuted?: boolean;
      audioInputDeviceId?: string;
      videoInputDeviceId?: string;
      // v1 호환 필드
      userIdentificationCode?: string;
      profileUrl?: string;
      companyFullpath?: string;
      isExternalParticipant?: boolean;
      userName2?: string;       // sendCommand 전용 사용자 이름 (displayName과 별도)
      avatar?: string;          // JSON.stringify({ label, value })
      externalUserId?: string;
      userContact?: string;
      userEmail?: string;
      isScreen?: boolean;
      userType?: string;
      host?: boolean;
      isTranslationMode?: string;
      enableAutoLang?: string;
      voiceNoteLocale?: string;
      employeeNo?: string;
      companyNo?: string;
    }
  ): Promise<void> {
    if (this._isDisposed) {
      throw new Error('ConferenceManager has been disposed');
    }

    this._handlers = handlers;
    this._initJitsiMeetJS();

    // 1. XMPP 연결 (mediaServerToken → Jitsi 인증)
    const connection = new Connection();
    this._connection = connection;

    const jitsiConnection = await connection.connect(
      roomId,
      options?.mediaServerToken,
      {
        onConnected: () => {
          console.log('[ConferenceManager] XMPP connected');
        },
        onFailed: (errorCode) => {
          handlers.onConnectionFailed(errorCode);
        },
        onDisconnected: (errorCode) => {
          handlers.onConnectionDisconnected();
          console.warn('[ConferenceManager] XMPP disconnected:', errorCode);
        },
      }
    );

    if (this._isDisposed) return;

    // 2. 회의 룸 입장
    const connector = new ConferenceConnector();
    this._connector = connector;

    // TrackContext 업데이트 콜백 주입 (VideoPage에서 설정)
    connector.onRemoteTrackAdded = this.onRemoteTrackAdded;
    connector.onRemoteTrackRemoved = this.onRemoteTrackRemoved;
    connector.onParticipantLeft = this.onParticipantLeft;

    // 디바이스 정보 수집
    await this._enumerateDevices(handlers, { reconcile: false });

    // 디바이스 핫플러그 감지 — 회의 중 새 디바이스 연결/해제 시 Redux 목록 갱신
    this._deviceChangeHandler = () => {
      if (this._deviceChangeTimer) {
        clearTimeout(this._deviceChangeTimer);
      }
      this._deviceChangeTimer = setTimeout(() => {
        this._deviceChangeTimer = null;
        this._enumerateDevices(handlers, { reconcile: true });
      }, 300);
    };
    navigator.mediaDevices.addEventListener('devicechange', this._deviceChangeHandler);

    await connector.connect(
      jitsiConnection,
      roomId,
      userName,
      {
        wehagoId: options?.wehagoId ?? '',
        nickname: options?.nickname ?? '',
        companyCode: options?.companyCode ?? '',
        avatarUrl: options?.avatarUrl ?? '',
        selectedAvatar: options?.selectedAvatar ?? '',
        nationality: options?.nationality ?? '',
        userNo: options?.userNo ?? '',
        audioInputDeviceId: options?.audioInputDeviceId || store.getState().local.audioInputDeviceId,
        videoInputDeviceId: options?.videoInputDeviceId || store.getState().local.videoInputDeviceId,
        startVideoMuted: options?.startVideoMuted ? 'true' : 'false',
        startAudioMuted: options?.startAudioMuted ? 'true' : 'false',
        // v1 호환 필드
        userIdentificationCode: options?.userIdentificationCode ?? '',
        profileUrl: options?.profileUrl ?? '',
        companyFullpath: options?.companyFullpath ?? '',
        isExternalParticipant: options?.isExternalParticipant ? 'true' : 'false',
        userName2: options?.userName2 ?? userName,
        avatar: options?.avatar ?? '',
        externalUserId: options?.externalUserId ?? '',
        userContact: options?.userContact ?? '',
        userEmail: options?.userEmail ?? '',
        isScreen: options?.isScreen ? 'true' : 'false',
        userType: options?.userType ?? '',
        host: options?.host ? 'true' : 'false',
        isTranslationMode: options?.isTranslationMode ?? 'F',
        enableAutoLang: options?.enableAutoLang ?? String(store.getState().translation.isAutoLangEnabled),
        voiceNoteLocale: options?.voiceNoteLocale ?? '',
        employeeNo: options?.employeeNo ?? '',
        companyNo: options?.companyNo ?? '',
      },
      handlers
    );
  }

  // ─── 디바이스 ────────────────────────────────────────────────────────────

  private async _enumerateDevices(
    handlers: ConferenceHandlers,
    options: { reconcile: boolean } = { reconcile: false }
  ): Promise<void> {
    return new Promise((resolve) => {
      try {
        window.JitsiMeetJS.mediaDevices.enumerateDevices((devices) => {
          const audioInput = devices.filter((d) => d.kind === 'audioinput');
          const videoInput = devices.filter((d) => d.kind === 'videoinput');
          const audioOutput = devices.filter((d) => d.kind === 'audiooutput');

          handlers.dispatch(
            localActions.setDevices({
              audioInputDevices: audioInput,
              videoInputDevices: videoInput,
              audioOutputDevices: audioOutput,
            })
          );

          if (options.reconcile) {
            this._reconcileAudioInputDevice(audioInput, handlers).catch((err) => {
              console.warn('[ConferenceManager] audio device reconcile failed:', err);
            });
          } else {
            this._syncInitialAudioInputSelection(audioInput, handlers);
          }

          resolve();
        });
      } catch {
        resolve();
      }
    });
  }

  private _getPreferredAudioInputDeviceId(devices: MediaDeviceInfo[]): string {
    return devices.find((d) => d.deviceId === 'default')?.deviceId
      ?? devices[0]?.deviceId
      ?? '';
  }

  private _syncInitialAudioInputSelection(
    audioInputDevices: MediaDeviceInfo[],
    handlers: ConferenceHandlers
  ): void {
    const selectedId = store.getState().local.audioInputDeviceId;
    if (!selectedId) {
      const preferredId = this._getPreferredAudioInputDeviceId(audioInputDevices);
      if (preferredId) {
        handlers.dispatch(localActions.setAudioInputDeviceId(preferredId));
      }
    }
    this._lastDefaultAudioInputLabel = audioInputDevices.find((d) => d.deviceId === 'default')?.label ?? '';
  }

  private async _reconcileAudioInputDevice(
    audioInputDevices: MediaDeviceInfo[],
    handlers: ConferenceHandlers
  ): Promise<void> {
    if (!this._connector || !this._handlers) return;

    const selectedId = store.getState().local.audioInputDeviceId;
    const hasSelectedDevice = selectedId
      ? audioInputDevices.some((device) => device.deviceId === selectedId)
      : false;
    const nextDefaultLabel = audioInputDevices.find((d) => d.deviceId === 'default')?.label ?? '';
    const defaultRouteChanged = Boolean(
      selectedId === 'default'
      && this._lastDefaultAudioInputLabel
      && nextDefaultLabel
      && this._lastDefaultAudioInputLabel !== nextDefaultLabel
    );

    this._lastDefaultAudioInputLabel = nextDefaultLabel;

    if (selectedId && hasSelectedDevice && !defaultRouteChanged) return;

    const nextDeviceId = hasSelectedDevice && selectedId
      ? selectedId
      : this._getPreferredAudioInputDeviceId(audioInputDevices);

    if (!nextDeviceId) {
      handlers.dispatch(localActions.setAudioInputDeviceId(''));
      return;
    }

    if (store.getState().local.isConnected) {
      await this._connector.replaceAudioTrack(nextDeviceId, this._handlers);
    } else {
      handlers.dispatch(localActions.setAudioInputDeviceId(nextDeviceId));
    }
  }

  async getConnectedDevices(): Promise<MediaDeviceInfo[]> {
    return new Promise((resolve) => {
      window.JitsiMeetJS.mediaDevices.enumerateDevices(resolve);
    });
  }

  getConnector(): ConferenceConnector | null {
    return this._connector;
  }

  async setAudioOutputDevice(deviceId: string): Promise<void> {
    await this._connector?.setAudioOutputDevice(deviceId);
  }

  // ─── 오디오/비디오 뮤트 ──────────────────────────────────────────────────

  async audioMute(): Promise<void> {
    await this._connector?.audioMute();
  }

  async audioUnmute(): Promise<void> {
    await this._connector?.audioUnmute();
  }

  async videoMute(): Promise<void> {
    await this._connector?.videoMute();
  }

  async videoUnmute(): Promise<void> {
    await this._connector?.videoUnmute();
  }

  // ─── 트랙 교체 ───────────────────────────────────────────────────────────

  async createAndReplaceAudioTrack(deviceId?: string): Promise<void> {
    if (!this._connector || !this._handlers) return;
    await this._connector.replaceAudioTrack(deviceId ?? '', this._handlers);
  }

  async createAndReplaceVideoTrack(deviceId?: string): Promise<void> {
    if (!this._connector || !this._handlers) return;
    await this._connector.replaceVideoTrack(deviceId ?? '', this._handlers);
  }

  async setVirtualBackgroundEffect(effect: { startEffect: (s: MediaStream) => MediaStream; stopEffect: () => void } | undefined): Promise<void> {
    const track = this._connector?.getLocalVideoTrack();
    if (!track || track.disposed) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (track as any).setEffect(effect);
  }

  /**
   * RNNoise 노이즈 제거 효과를 오디오 트랙에 적용/해제한다.
   * @param effect - NoiseSuppressionEffect 인스턴스 또는 undefined(해제)
   */
  async setNoiseSuppressionEffect(effect: { startEffect: (s: MediaStream) => MediaStream; stopEffect: () => void; isEnabled: (t: unknown) => boolean } | undefined): Promise<void> {
    const track = this._connector?.getLocalAudioTrack();
    if (!track || track.disposed) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (track as any).setEffect(effect);
  }

  /**
   * 노이즈 제거 활성/비활성 전환 (ConferenceConnector에 위임).
   */
  async setNoiseSuppressionEnabled(enabled: boolean): Promise<void> {
    await this._connector?.setNoiseSuppressionEnabled(enabled);
  }

  isNoiseSuppressionEnabled(): boolean {
    return this._connector?.isNoiseSuppressionEnabled() ?? true;
  }

  // ─── 화면 공유 ───────────────────────────────────────────────────────────

  async startScreenShare(): Promise<void> {
    if (!this._connector || !this._handlers) return;
    await this._connector.startScreenShare(this._handlers);
  }

  async stopScreenShare(): Promise<void> {
    if (!this._connector || !this._handlers) return;
    await this._connector.stopScreenShare(this._handlers);
  }

  // ─── 참여자 제어 ─────────────────────────────────────────────────────────

  kickParticipant(userId: string): void {
    // 강퇴 후에는 participantLeft로 스토어에서 제거되므로 먼저 대상 정보를 캡처한다
    const state = store.getState();
    const target = state.participants.participants[userId];
    const token = state.local.token;

    this._connector?.kickParticipant(userId);

    if (!token || !target) return;

    // wehagoid 커맨드 수신 전이면 신원 필드가 비어 있을 수 있다.
    // 서버가 videoseq로 접속 이력에서 보정하므로 아는 값만 보낸다.
    const userType = Number(target.userType) || undefined;

    // 로그 적재 실패가 강퇴를 막지 않도록 결과를 기다리지 않는다
    apiManager
      .addKickedUser(token, {
        videoseq: userId,
        user_id: target.wehagoId || target.externalUserId || undefined,
        user_name: target.displayName || target.nickname || target.userName || undefined,
        user_type: userType,
      })
      .catch((err) => console.warn('[ConferenceManager] 강퇴 로그 적재 실패:', err));
  }

  muteRemoteAudio(userId: string): void {
    this._connector?.muteRemoteAudio(userId);
  }

  unmuteRemoteAudio(userId: string): void {
    this._connector?.unmuteRemoteAudio(userId);
  }

  muteRemoteVideo(userId: string): void {
    this._connector?.muteRemoteVideo(userId);
  }

  muteAllAudio(): void {
    this._connector?.muteAllAudio();
  }

  muteAllVideo(): void {
    this._connector?.muteAllVideo();
  }

  requestUnmute(userId: string, mediaType: 'audio' | 'video'): void {
    this._connector?.requestUnmute(userId, mediaType);
  }

  requestUnmuteAll(mediaType: 'audio' | 'video'): void {
    this._connector?.requestUnmuteAll(mediaType);
  }

  selectParticipant(userId: string | null): void {
    this._connector?.selectParticipant(userId);
  }

  updateReceiverQuality(
    screenMode: 'GRID' | 'SIDE' | 'PRESENTER',
    mainUserId: string | null,
    visibleCount: number,
    overrides?: { main?: number | null; sub?: number | null; gridTiers?: import('./config/videoQualityPolicy').GridTier[] | null; gridFallback?: import('./config/videoQualityPolicy').VideoQualityLevel | null },
  ): void {
    this._connector?.updateReceiverQuality(screenMode, mainUserId, visibleCount, overrides);
  }

  // ─── 채팅 ────────────────────────────────────────────────────────────────

  sendChatMessage(message: string, to?: string): void {
    this._connector?.sendChatMessage(message, to);
  }

  /** STT 음성 텍스트를 Jitsi room.sendMessage로 전송 (원본 room.sendTextMessage) */
  sendSpeechMessage(jsonPayload: string): void {
    this._connector?.sendChatMessage(jsonPayload);
  }

  // ─── 발언권 ──────────────────────────────────────────────────────────────

  sendFloorRequest(): void {
    const room = this._connector?.getRoom();
    room?.sendCommand(CONFERENCE_COMMANDS.REQUEST_FLOOR, {
      value: room.myUserId(),
    });
  }

  sendFloorGrant(userId: string): void {
    this._connector?.sendCommand(CONFERENCE_COMMANDS.GRANT_FLOOR, {
      value: userId,
      attributes: {
        targetUser: JSON.stringify({ id: userId }),
        type: 'accept',
      },
    });
  }

  sendFloorGrantTarget(userId: string, type: 'accept' | 'reject'): void {
    this._connector?.sendCommand(CONFERENCE_COMMANDS.GRANT_FLOOR_TARGET, {
      value: userId,
      attributes: {
        targetUser: JSON.stringify({ id: userId }),
        type,
      },
    });
  }

  sendFloorStop(userId: string): void {
    this._connector?.sendCommand(CONFERENCE_COMMANDS.STOP_FLOOR, {
      value: userId,
      attributes: {
        targetUser: JSON.stringify({ id: userId }),
      },
    });
  }

  // ─── 녹화 ────────────────────────────────────────────────────────────────

  async startRecording(options: unknown): Promise<void> {
    await this._connector?.startRecording(options);
  }

  async stopRecording(sessionId: string): Promise<void> {
    await this._connector?.stopRecording(sessionId);
  }

  // ─── 룸 커맨드 ───────────────────────────────────────────────────────────

  sendCommand(command: string, values: unknown): void {
    this._connector?.sendCommand(command, values);
  }

  sendCommandOnce(command: string, values: unknown): void {
    this._connector?.sendCommandOnce(command, values);
  }

  removeCommand(command: string): void {
    this._connector?.removeCommand(command);
  }

  // ─── 유틸 ────────────────────────────────────────────────────────────────

  getRoom(): JitsiConference | null {
    return this._connector?.getRoom() ?? null;
  }

  // ─── Dispose ─────────────────────────────────────────────────────────────

  async dispose(): Promise<void> {
    if (this._isDisposed) return;
    this._isDisposed = true;

    // 디바이스 변경 리스너 해제
    if (this._deviceChangeHandler) {
      navigator.mediaDevices.removeEventListener('devicechange', this._deviceChangeHandler);
      this._deviceChangeHandler = null;
    }

    if (this._deviceChangeTimer) {
      clearTimeout(this._deviceChangeTimer);
      this._deviceChangeTimer = null;
    }

    // ConferenceConnector dispose (트랙, 룸 퇴장, Redux 상태 정리)
    if (this._connector) {
      await this._connector.dispose(this._handlers ?? undefined);
      this._connector = null;
    }

    // XMPP 연결 해제
    if (this._connection) {
      this._connection.dispose();
      this._connection = null;
    }

    this._handlers = null;
    console.log('[ConferenceManager] disposed');
  }
}

export default ConferenceManager;
